import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Label, Tracking } from '@trivio/contracts';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { useDashboardSessionKey } from '../../app/DashboardSessionKeyContext';
import { ApiProblemError, cancelLabel, getLabel, getLabelDocument, getTracking } from '../../lib/api-client';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { TrackingTimeline } from './TrackingTimeline';
import { labelFailureReasonPt, labelStatusBadgeVariant, labelStatusPt, providerIdPt } from '../../utils/enums-ptbr';
import { formatCentsToBRL } from '../../utils/money';

function extractProblemMessage(err: unknown, fallback: string): string {
  return err instanceof ApiProblemError ? (err.problem.detail ?? err.problem.title) : fallback;
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  window.document.body.appendChild(anchor);
  anchor.click();
  window.document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** `/labels/:labelId` (TTP-244) — label details + rastreio timeline + raw payload toggle. */
export function LabelDetailPage() {
  const { labelId } = useParams<{ labelId: string }>();
  const { key } = useDashboardSessionKey();
  const apiKey = key?.key ?? null;

  const [label, setLabel] = useState<Label | null>(null);
  const [tracking, setTracking] = useState<Tracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackingError, setTrackingError] = useState<string | null>(null);

  const [actionError, setActionError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const loadTracking = useCallback(() => {
    if (!apiKey || !labelId) return;
    setTrackingLoading(true);
    setTrackingError(null);
    getTracking(apiKey, labelId)
      .then(setTracking)
      .catch((err: unknown) => setTrackingError(extractProblemMessage(err, 'Não foi possível carregar o rastreio agora.')))
      .finally(() => setTrackingLoading(false));
  }, [apiKey, labelId]);

  useEffect(() => {
    if (!apiKey || !labelId) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getLabel(apiKey, labelId)
      .then((result) => {
        if (!cancelled) setLabel(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(extractProblemMessage(err, 'Não foi possível carregar a etiqueta.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey, labelId]);

  useEffect(() => {
    loadTracking();
  }, [loadTracking]);

  async function handleDownload() {
    if (!apiKey || !label) return;
    setActionError(null);
    setDownloading(true);
    try {
      const document_ = await getLabelDocument(apiKey, label.label_id, 'pdf');
      triggerBrowserDownload(document_.blob, `etiqueta-${label.label_id}.pdf`);
    } catch (err) {
      setActionError(extractProblemMessage(err, 'Não foi possível baixar o PDF agora.'));
    } finally {
      setDownloading(false);
    }
  }

  async function handleConfirmCancel() {
    if (!apiKey || !label) return;
    setCanceling(true);
    setActionError(null);
    try {
      const updated = await cancelLabel(apiKey, label.label_id);
      setLabel(updated);
      setCancelOpen(false);
    } catch (err) {
      setActionError(extractProblemMessage(err, 'Não foi possível cancelar a etiqueta agora.'));
    } finally {
      setCanceling(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Link to="/labels" className="text-sm font-medium text-primary underline-offset-4 hover:underline">
          ← Voltar para Etiquetas
        </Link>
        <h1 className="text-2xl font-medium text-foreground">Detalhe da etiqueta</h1>
      </div>

      {actionError ? (
        <Alert variant="error" title="Ação não concluída">
          {actionError}
        </Alert>
      ) : null}

      {loading ? (
        <LoadingState label="Carregando etiqueta…" />
      ) : error ? (
        <Alert variant="error" title="Não foi possível carregar a etiqueta">
          {error}
        </Alert>
      ) : label ? (
        <>
          <Card className="flex flex-col gap-4 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Badge variant={labelStatusBadgeVariant(label.status)}>{labelStatusPt(label.status)}</Badge>
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={label.status !== 'ready' || downloading} onClick={handleDownload}>
                  {downloading ? 'Baixando…' : 'Baixar PDF'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={label.status === 'canceled' || label.status === 'failed'}
                  onClick={() => setCancelOpen(true)}
                >
                  Cancelar etiqueta
                </Button>
              </div>
            </div>

            {label.status === 'failed' && label.failure_reason ? (
              <p className="text-sm font-medium text-accent">{labelFailureReasonPt(label.failure_reason)}</p>
            ) : null}

            <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
              <div>
                <dt className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Provedor</dt>
                <dd className="text-foreground">{providerIdPt(label.provider)}</dd>
              </div>
              <div>
                <dt className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Transportadora</dt>
                <dd className="text-foreground">{label.carrier}</dd>
              </div>
              <div>
                <dt className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Serviço</dt>
                <dd className="text-foreground">{label.service}</dd>
              </div>
              <div>
                <dt className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Preço</dt>
                <dd className="text-foreground">{formatCentsToBRL(label.price_cents)}</dd>
              </div>
              <div>
                <dt className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Código de rastreio</dt>
                <dd className="font-mono text-foreground">{label.tracking_code ?? '—'}</dd>
              </div>
              <div>
                <dt className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Referência externa</dt>
                <dd className="text-foreground">{label.external_ref ?? '—'}</dd>
              </div>
              <div>
                <dt className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Criada em</dt>
                <dd className="text-foreground">{new Date(label.created_at).toLocaleString('pt-BR')}</dd>
              </div>
              <div>
                <dt className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Atualizada em</dt>
                <dd className="text-foreground">{new Date(label.updated_at).toLocaleString('pt-BR')}</dd>
              </div>
            </dl>
          </Card>

          <Card className="flex flex-col gap-4 p-6">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-medium text-foreground">Rastreio</h2>
              <Button type="button" variant="outline" size="sm" onClick={loadTracking} disabled={trackingLoading}>
                {trackingLoading ? 'Atualizando…' : 'Atualizar'}
              </Button>
            </div>
            {trackingError ? (
              <Alert variant="error" title="Não foi possível carregar o rastreio">
                {trackingError}
              </Alert>
            ) : trackingLoading && !tracking ? (
              <LoadingState label="Carregando rastreio…" />
            ) : tracking ? (
              <TrackingTimeline events={tracking.events} />
            ) : (
              <p className="text-sm text-muted-foreground">Sem dados de rastreio ainda.</p>
            )}
          </Card>
        </>
      ) : null}

      <ConfirmDialog
        open={cancelOpen}
        title="Cancelar etiqueta"
        description="Tem certeza que deseja cancelar esta etiqueta? Essa ação não pode ser desfeita."
        confirmLabel="Cancelar etiqueta"
        cancelLabel="Voltar"
        loading={canceling}
        onConfirm={handleConfirmCancel}
        onCancel={() => setCancelOpen(false)}
      />
    </div>
  );
}
